// src/client/renderer/vfx/env/ceremony-events.ts
//
// Plan 18, Task 1.1 — Ceremony event union + audio cue table.
//
// Decision E2 (from Plan 18 preamble): ceremony events are NOT variants of
// GameEvent — they ride a separate adapter bus Plan 22 / Plan 24c instantiates.
// Plan 18 only DECLARES this union.

import type { AudioCueId } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// Ceremony event discriminated union
// ─────────────────────────────────────────────────────────────────────────────

/** The 4 ceremony events Plan 18's CEREMONY pools subscribe to. Decision E2:
 *  these are NOT variants of GameEvent — they ride a separate adapter bus
 *  Plan 22 / Plan 24c instantiates. Plan 18 only DECLARES this union. */
export interface AwardsCeremonyStartEvent {
  type: 'awards_ceremony_start';
  totalCategories: number;
}
export interface AwardRevealedEvent {
  type: 'award_revealed';
  category: string; // matches AwardGrantedEvent.category surface
  recipientPlayerId: string;
  outcome: 'most' | 'least' | 'random';
}
export interface TrophyShineEvent {
  type: 'trophy_shine';
  recipientPlayerId: string;
}
export interface GameOverEvent {
  type: 'game_over';
  winnerPlayerId: string | null; // null if no winner (rare)
}

export type CeremonyEvent =
  | AwardsCeremonyStartEvent
  | AwardRevealedEvent
  | TrophyShineEvent
  | GameOverEvent;

export type CeremonyEventType = CeremonyEvent['type'];

// ─────────────────────────────────────────────────────────────────────────────
// Audio cue table — canonical contract surface for per-pool resolvers in Phase 5
// ─────────────────────────────────────────────────────────────────────────────

export const CEREMONY_AUDIO_CUES: Record<CeremonyEventType, AudioCueId> = {
  awards_ceremony_start: 'sfx.awards_intro',
  award_revealed: 'sfx.award_reveal',
  trophy_shine: 'sfx.trophy_shine',
  game_over: 'sfx.fanfare_finale',
} as const;

export function audioCueForCeremony(t: CeremonyEventType): AudioCueId {
  return CEREMONY_AUDIO_CUES[t];
}
